import type {
  GetApiV1ThreeLetterCodePartnerAdminsClientsClientId200,
  GetApiV1ThreeLetterCodePartnerAdminsGroups200,
  GetApiV1ThreeLetterCodePartnerAdminsPartnerBranches200,
  GetApiV1ThreeLetterCodePartnerAdminsPartnerFeatureFlags200,
  GetApiV1ThreeLetterCodePublicPrefectures200,
} from '@fukuri-admins/apis/client/gen/model';
import { getFeatureFlagLabel } from '~/utils/clientFeatureFlagUtils';
import type { EditClientPresentation } from '../../presentations';

type PresentationProps = React.ComponentProps<typeof EditClientPresentation>;

type IConvertToPresentationProps = (args: {
  /** 都道府県一覧 */
  prefectures: GetApiV1ThreeLetterCodePublicPrefectures200;
  /** 取引店舗一覧 */
  partnerBranches: GetApiV1ThreeLetterCodePartnerAdminsPartnerBranches200;
  /** グループ一覧 */
  groups: GetApiV1ThreeLetterCodePartnerAdminsGroups200;
  /** クライアント詳細 */
  clientData: GetApiV1ThreeLetterCodePartnerAdminsClientsClientId200;
  /** パートナーの機能フラグ */
  partnerFeatureFlags: GetApiV1ThreeLetterCodePartnerAdminsPartnerFeatureFlags200;
}) => {
  prefectureOptions: PresentationProps['prefectureOptions'];
  partnerBranchOptions: PresentationProps['partnerBranchOptions'];
  groupOptions: PresentationProps['groupOptions'];
  featureFlagOptions: PresentationProps['featureFlagOptions'];
};

/**
 * hooks の状態をプレゼンテーション用の props に変換する（都道府県 / 取引店舗 / グループ / 機能フラグ）
 *
 * featureFlagOptions は partner_feature_flag が有効な機能のみを対象にフィルタする（PEN-914）。
 * パートナーが契約していない機能のチェックボックスを非表示にするため、
 * disabled 付与ではなく filter で除外する方式に変更した。
 */
export const convertToPresentationProps: IConvertToPresentationProps = ({
  prefectures,
  partnerBranches,
  groups,
  clientData,
  partnerFeatureFlags,
}) => ({
  prefectureOptions: prefectures.prefectures
    .filter((p): p is Required<typeof p> => !!p.code && !!p.name)
    .map(({ code, name }) => ({ value: code, label: name })),
  partnerBranchOptions: partnerBranches.partner_branches.map(({ id, name }) => ({
    value: String(id),
    label: name,
  })),
  groupOptions: groups.groups.map(({ id, name, code }) => ({
    value: String(id),
    label: `${name}（${code}）`,
  })),
  featureFlagOptions: (clientData.client.client_feature_flags ?? [])
    .filter(({ feature_key }) =>
      partnerFeatureFlags.partner_feature_flags.some(
        pf => pf.feature_key === feature_key && pf.enabled
      )
    )
    .map(({ feature_key }) => ({
      value: feature_key,
      label: getFeatureFlagLabel(feature_key),
    })),
});
